import React, { useCallback, useState } from 'react';

import Icon from '../Icon';
import IconButton from '../IconButton';
import Label from '../Label';
import './InputNumber.css';

/**
 *  React Number Input component'
 * it has two props, value and onChange
 * value is a number value
 * onChange is a function that will be called when the number input is changed
 * it can get changed by clicking on the up and down buttons
 * or by typing a number in the input
 */

const sizesClasses = {
	sm: 'w-[45px] h-[24px]',
	lg: 'w-[206px] h-[35px]',
};

const InputNumber: React.FC<{
	value: number;
	onChange: (value: number) => void;
	minValue?: number;
	maxValue?: number;
	step?: number;
	size?: 'sm' | 'lg';
	className?: string;
	inputClassname?: string;
	labelClassName?: string;
	label?: string;
	showAdjustmentArrows?: boolean;
	disabled?: boolean;
}> = ({
	value,
	onChange,
	step = 1,
	className,
	inputClassname,
	size = 'sm',
	minValue = 0,
	maxValue = 100,
	labelClassName,
	label,
	showAdjustmentArrows = true,
	disabled,
}) => {
	// Chỉ lưu chuỗi nhập trung gian (ví dụ: '-', '', '.') khi đang focus
	const [localInput, setLocalInput] = useState<string | null>(null);
	const [isFocused, setIsFocused] = useState(false);

	const decimalPlaces = Number.isInteger(step) ? 0 : step.toString().split('.')[1].length;

	// Giá trị hiển thị được tính inline tại render, không cần state/useEffect
	const displayValue = isFocused && localInput !== null ? localInput : parseFloat(String(value)).toFixed(decimalPlaces);

	const handleMinMax = useCallback((val: number) => Math.min(Math.max(val, minValue), maxValue), [maxValue, minValue]);

	const updateValue = (val: number) => {
		const newValue = handleMinMax(val);
		onChange(newValue);
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value;

		// Cho phép ký tự trung gian khi người dùng đang nhập
		if (inputValue === '-' || inputValue === '' || inputValue === '.') {
			setLocalInput(inputValue);
			return;
		}

		const number = Number(inputValue);

		// Bỏ qua giá trị không hợp lệ như 'NaN'
		if (!isNaN(number)) {
			const newValue = handleMinMax(number);
			setLocalInput(String(newValue));
			onChange(newValue);
		}
	};

	const handleFocus = () => {
		setLocalInput(String(value));
		setIsFocused(true);
	};

	const handleBlur = () => {
		setIsFocused(false);
		setLocalInput(null);
	};

	const increment = () => updateValue(value + step);
	const decrement = () => updateValue(value - step);

	return (
		<div className="flex flex-1 flex-col">
			{label && <Label className={labelClassName} text={label} />}
			<div
				className={`items-centerrounded flex border border-common-light bg-primary-dark px-1 ${
					sizesClasses[size]
				} ${className || ''} `}
			>
				<div className="flex w-full">
					<input
						type="number"
						value={displayValue}
						step={step}
						onFocus={handleFocus}
						onBlur={handleBlur}
						onChange={handleChange}
						className={`input-number w-full bg-primary-dark px-1 text-[13px] text-white ${inputClassname} ${disabled && 'cursor-not-allowed'}`}
						disabled={disabled}
					/>
					{showAdjustmentArrows && !disabled && (
						<div className="up-arrowsize flex flex-col items-center justify-around">
							<ArrowButton onClick={increment} rotate />
							<ArrowButton onClick={decrement} />
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

const ArrowButton = ({ onClick, rotate = false }: { onClick: () => void; rotate?: boolean }) => (
	<IconButton
		id="arrow-icon"
		variant="text"
		color="inherit"
		size="initial"
		className={` ${rotate ? 'rotate-180 transform' : ''} bg-transparent hover:!text-primary-active`}
		onClick={onClick}
	>
		<Icon name="ui-arrow-down" />
	</IconButton>
);

export default InputNumber;
